import { format, isThisYear, isToday, isYesterday } from 'date-fns'
import { enUS, vi } from 'date-fns/locale'
import type { Locale } from 'date-fns'

/**
 * Dates are formatted outside React, so the locale is set imperatively when the
 * language changes rather than threaded through every call site. An English
 * "Yesterday" sitting in a Vietnamese conversation list reads as a bug.
 */
const LOCALES: Record<string, Locale> = { vi, en: enUS }
let active: Locale = vi
let strings = { now: 'vừa xong', yesterday: 'Hôm qua', today: 'Hôm nay' }

export function setDateLocale(lang: 'vi' | 'en') {
  active = LOCALES[lang] ?? vi
  strings =
    lang === 'en'
      ? { now: 'now', yesterday: 'Yesterday', today: 'Today' }
      : { now: 'vừa xong', yesterday: 'Hôm qua', today: 'Hôm nay' }
}

/** Compact timestamps for lists: "3m", "14:02", "Mon", "12 Mar". */
export function relativeTime(iso: string): string {
  const d = new Date(iso)
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000)
  if (seconds < 60) return strings.now
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  if (isToday(d)) return format(d, 'HH:mm', { locale: active })
  if (isYesterday(d)) return strings.yesterday
  if (seconds < 7 * 86400) return format(d, 'EEE', { locale: active })
  return format(d, isThisYear(d) ? 'd MMM' : 'd MMM yyyy', { locale: active })
}

/** Day separators inside a conversation. */
export function dayLabel(iso: string): string {
  const d = new Date(iso)
  if (isToday(d)) return strings.today
  if (isYesterday(d)) return strings.yesterday
  return format(d, isThisYear(d) ? 'EEEE, d MMMM' : 'd MMMM yyyy', { locale: active })
}

export function clockTime(iso: string): string {
  return format(new Date(iso), 'HH:mm')
}

export function sameDay(a: string, b: string): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString()
}

export function callDuration(startedAt: string, endedAt?: string | null): string {
  const ms = (endedAt ? new Date(endedAt).getTime() : Date.now()) - new Date(startedAt).getTime()
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n: number) => n.toString().padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}
